import { ActionReducerMapBuilder, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchMyReads, fetchMyReadsUnreadCount, markAllRead, markPostRead } from "../actions/myReadsActions";

const initialPagination: BlogPostsPaginationType = {
  page: 1,
  page_size: 20,
  total_count: 0,
  total_pages: 1,
  has_next: false,
  has_previous: false,
};

const initialState: MyReadsStateType = {
  posts: [],
  bookmarkedBlogCount: 0,
  unreadCount: 0,
  pagination: initialPagination,
  status: 'idle',
  error: null,
  latestFetchRequestId: null,
  latestMarkRequestIdByPostId: {},
};

const myReadsSlice = createSlice({
  name: 'myReads',
  initialState,
  reducers: {
    setMyReadsUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
  },
  extraReducers: builder => {
    getMyReadsBuilder(builder);
    getMyReadsUnreadCountBuilder(builder);
    markPostReadBuilder(builder);
    markAllReadBuilder(builder);
  },
});

const getMyReadsBuilder = (builder: ActionReducerMapBuilder<MyReadsStateType>) => {
  // Rapid tab/page switching can fire several fetchMyReads calls in a row;
  // whichever settles first is no longer the one the user cares about. Only
  // the most recently dispatched request's requestId is tracked, and
  // fulfilled/rejected below ignore any response whose requestId doesn't
  // match it, so a stale response can never clobber newer state.
  builder.addCase(fetchMyReads.pending, (state, action) => {
    state.status = 'pending';
    state.error = null;
    state.latestFetchRequestId = action.meta.requestId;
  });
  builder.addCase(
    fetchMyReads.fulfilled,
    (
      state,
      action: PayloadAction<
        {
          bookmarkedBlogCount: number;
          unreadCount: number;
          posts: MyReadsPostType[];
          pagination: BlogPostsPaginationType;
        },
        string,
        { requestId: string }
      >
    ) => {
      if (action.meta.requestId !== state.latestFetchRequestId) {
        return;
      }
      state.status = 'succeeded';
      state.posts = action.payload.posts;
      state.bookmarkedBlogCount = action.payload.bookmarkedBlogCount;
      // Server-computed across all pages, not just the current one.
      state.unreadCount = action.payload.unreadCount;
      state.pagination = action.payload.pagination;
      state.error = null;
    }
  );
  builder.addCase(fetchMyReads.rejected, (state, action) => {
    if (action.meta.requestId !== state.latestFetchRequestId) {
      return;
    }
    state.status = 'failed';
    state.error = action.payload || 'failed to fetch my reads';
  });
};

const getMyReadsUnreadCountBuilder = (builder: ActionReducerMapBuilder<MyReadsStateType>) => {
  // Deliberately only touches `unreadCount` - see the note on
  // fetchMyReadsUnreadCount for why it must not write posts/pagination.
  builder.addCase(fetchMyReadsUnreadCount.fulfilled, (state, action: PayloadAction<number>) => {
    state.unreadCount = action.payload;
  });
  builder.addCase(fetchMyReadsUnreadCount.rejected, (state, action) => {
    console.error('Failed to fetch my reads unread count:', action.payload);
  });
};

const markPostReadBuilder = (builder: ActionReducerMapBuilder<MyReadsStateType>) => {
  // Optimistic: flip the post's read state as soon as the request goes out,
  // rather than waiting for the round-trip - the checkbox/click should feel
  // instant. `rejected` below undoes it if the request actually failed.
  // `unreadCount` is a server-computed total across all pages, so it's
  // nudged by +/-1 here rather than recomputed from the current (partial) page.
  //
  // A rapid double-toggle of the same post (e.g. checking then unchecking
  // before the first request completes) can have its two requests resolve
  // out of order. `latestMarkRequestIdByPostId` tracks the newest request
  // per post so fulfilled/rejected can ignore a stale one instead of
  // clobbering state a newer request already settled correctly.
  builder.addCase(markPostRead.pending, (state, action) => {
    const { postId, isRead } = action.meta.arg;
    state.latestMarkRequestIdByPostId[postId] = action.meta.requestId;
    const post = state.posts.find(p => p.id === postId);
    if (post && post.is_read !== isRead) {
      state.unreadCount = Math.max(0, state.unreadCount + (isRead ? -1 : 1));
    }
    if (post) {
      post.is_read = isRead;
    }
  });
  builder.addCase(markPostRead.fulfilled, (state, action) => {
    const { postId, isRead } = action.payload;
    if (action.meta.requestId !== state.latestMarkRequestIdByPostId[postId]) {
      return;
    }
    const post = state.posts.find(p => p.id === postId);
    if (post) {
      post.is_read = isRead;
    }
  });
  builder.addCase(markPostRead.rejected, (state, action) => {
    console.error('Failed to mark post read/unread:', action.payload);
    const { postId, isRead } = action.meta.arg;
    if (action.meta.requestId !== state.latestMarkRequestIdByPostId[postId]) {
      return;
    }
    const post = state.posts.find(p => p.id === postId);
    // Revert the optimistic flip from `pending` back to its prior value.
    const reverted = !isRead;
    if (post && post.is_read !== reverted) {
      state.unreadCount = Math.max(0, state.unreadCount + (reverted ? -1 : 1));
    }
    if (post) {
      post.is_read = reverted;
    }
  });
};

const markAllReadBuilder = (builder: ActionReducerMapBuilder<MyReadsStateType>) => {
  // Applied on confirmed success only (not optimistically on `pending`) since
  // there's no cheap way to revert a bulk flip on failure - MyReadsPage
  // re-fetches the current tab right after this resolves either way, so the
  // list itself stays correct regardless.
  builder.addCase(markAllRead.fulfilled, state => {
    state.unreadCount = 0;
    state.posts.forEach(post => {
      post.is_read = true;
    });
  });
  builder.addCase(markAllRead.rejected, (state, action) => {
    console.error('Failed to mark all as read:', action.payload);
  });
};

export const { setMyReadsUnreadCount } = myReadsSlice.actions;
export default myReadsSlice.reducer;
